import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { delay, distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs/operators';

/** Requests that settle faster than this never show a spinner at all. */
export const LOADER_SHOW_DELAY_MS = 150;

/**
 * How long the loader stays up after the last request settles. Bridges chained
 * requests so a follow-up starting within this window keeps the same spinner,
 * and guarantees a minimum visible time once a spinner has appeared.
 */
export const LOADER_HIDE_GRACE_MS = 300;

/**
 * Reference-counted global loading state driven by the LoadingInterceptor.
 * Concurrent requests keep the loader visible until the last one settles, and
 * `loading$` is shared so every subscriber sees the same single spinner.
 */
@Injectable({ providedIn: 'root' })
export class LoadingService {
  private activeRequests = new BehaviorSubject<number>(0);

  readonly loading$: Observable<boolean> = this.activeRequests.pipe(
    map(activeRequests => activeRequests > 0),
    distinctUntilChanged(),
    switchMap((loading, index) =>
      index === 0 ? of(loading) : of(loading).pipe(delay(loading ? LOADER_SHOW_DELAY_MS : LOADER_HIDE_GRACE_MS))
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  show(): void {
    this.activeRequests.next(this.activeRequests.value + 1);
  }

  hide(): void {
    if (this.activeRequests.value > 0) {
      this.activeRequests.next(this.activeRequests.value - 1);
    }
  }
}
